import { HttpClient, HttpHeaders } from '@angular/common/http';
import { of } from 'rxjs';
import { ActionReducer, MetaReducer, Action } from '@ngrx/store';
import { catchError, retry } from 'rxjs/operators';

import {
  DnvMapActionType, DnvLayerActionType, SetActiveLayer, SetAvailableLayer,
  MapViewActionType, legendActionType, dnvSearchActionType
} from 'dnv-lib';
import { State, Tracking, ApplicationActionType, PropertiesActionType, GetMailMergeList } from 'src/app/state-management';
import { environment } from 'src/environments/environment';


const trackingUrl = 'api/tracking/record';

const httpOptions = {
  headers: new HttpHeaders({
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache'
  })
};

export function trackingFactory(httpClient: HttpClient): MetaReducer<any> {
  return (reducer: ActionReducer<any>): ActionReducer<any> => {
    return (state: State, action: Action) => {
      switch (action.type) {
        // Explicitly list actions that are not tracked below:
        case ApplicationActionType.DelayedAction:
        case ApplicationActionType.DelayedActionBy1:
        case ApplicationActionType.Idle:
        case ApplicationActionType.Layer:
        case ApplicationActionType.Search:
        case ApplicationActionType.Legend:
        // case ApplicationActionType.Toolbox:
        // case ApplicationActionType.Draw:
        // case ApplicationActionType.Measure:
        // case ApplicationActionType.Table:
        case ApplicationActionType.GetLayers:
        case ApplicationActionType.CheckLayerDisplay:
        case ApplicationActionType.SetSearchCoordinates:
        case ApplicationActionType.SearchCoordinates:
        case ApplicationActionType.SearchCoordinatesSuccess:
        case ApplicationActionType.SearchCoordinatesError:
        case ApplicationActionType.SearchFromCoordinates:
        case ApplicationActionType.SearchFromCoordinatesSuccess:
        case ApplicationActionType.SearchFromCoordinatesError:
        case ApplicationActionType.GetShape:
        case ApplicationActionType.GetDBLink:
        case ApplicationActionType.GridSelectLayer:
        case ApplicationActionType.GetGridSelectLayerSuccess:
        case ApplicationActionType.GetGridSelectLayerError:
        case ApplicationActionType.InvalidateGridCache:
        case ApplicationActionType.UpdateWindowSize:
        case ApplicationActionType.GetBasemaps:
        case ApplicationActionType.GetMetaData:
        case ApplicationActionType.GetDataNames:
        // case ApplicationActionType.ToastUserNotification:
        case ApplicationActionType.IdentifySurroundingFeature:
        case ApplicationActionType.GetFeatureData:
        case ApplicationActionType.SetSurroundingFeature:
        case ApplicationActionType.SetSelectedFeatureSections:
        case ApplicationActionType.ConcatSelectedFeatureSections:
        // case ApplicationActionType.Export:
        // case ApplicationActionType.EngDraw:
        case ApplicationActionType.AppMapViewLoadFinished:
        // case ApplicationActionType.SearchEngineerDrawings:
        // case ApplicationActionType.SearchEngineerDrawingsByNum:
        // case ApplicationActionType.SearchEngineerDrawingsByProj:
        case ApplicationActionType.GetEngDrawingGeometry:
        // case ApplicationActionType.OpenEngDrawing:
        case ApplicationActionType.OpenMapView:
        case ApplicationActionType.RenewArcGisToken:
        case ApplicationActionType.RenewArcGisTokenSuccess:
        case ApplicationActionType.RenewArcGisTokenError:
        case ApplicationActionType.SearchCCTV:
        case ApplicationActionType.SetCCTV:
        case ApplicationActionType.SelectCCTV:
        case ApplicationActionType.SearchCCTVNotes:
        case ApplicationActionType.SetCCTVNotes:
        case ApplicationActionType.SetToolboxStatus:
        case ApplicationActionType.GetMailMergeByProperty:
        case ApplicationActionType.GetMailMergeByArea:
        // case ApplicationActionType.GetMailMergeList:
        case ApplicationActionType.ToolboxMailMerge:
        case ApplicationActionType.ToolboxMailMergeArea:
        case ApplicationActionType.ToolboxMailMergeBuffer:
        case ApplicationActionType.ToolboxMailMergeSelectedParcels:
        case ApplicationActionType.ToolboxMailMergeToggleParcelAtCoordinate:
        case ApplicationActionType.ToolboxMailMergeToggleParcel:
        case ApplicationActionType.RefreshDisplayedGeoJson:
        case ApplicationActionType.SetMailMergeList:
        case ApplicationActionType.SetMailMergeDBLinks:
        case ApplicationActionType.LaunchStreetView:
        case ApplicationActionType.ToggleStreetView:
        case ApplicationActionType.SetMeasurements:
        case ApplicationActionType.SetFeatureRasterHighlight:
        case ApplicationActionType.GetCoordsByObjectId:
        case ApplicationActionType.SetEditFeature:
        case ApplicationActionType.GetEditPermission:
        case ApplicationActionType.SetEditPermission:
        case ApplicationActionType.SetNewFeatureLatLng:
        case ApplicationActionType.SetLastEditFeature:
        // case ApplicationActionType.SaveEditFeature:
        // case ApplicationActionType.UpdateEditFeature:
        // case ApplicationActionType.DeleteEditFeature:
        case ApplicationActionType.GetMaxAssetId:
        case ApplicationActionType.SetMaxAssetId:
        // case ApplicationActionType.UpdateEditFeatureGeometry:
        case ApplicationActionType.SetShowSpinner:
        case ApplicationActionType.SetUserProfile:


        case DnvMapActionType.SetMapState:
        case DnvMapActionType.SetMapBasemaps:
        case DnvMapActionType.SetMapFeatureLayers:
        case DnvMapActionType.SetMapGeoJsonLayers:
        case DnvMapActionType.SetMarkers:
        case DnvMapActionType.CenterMap:
        case DnvMapActionType.FitBounds:
        case DnvMapActionType.SetBounds:
        case DnvMapActionType.SetZoom:
        case DnvMapActionType.ZoomToGeoJsonLayer:
        case DnvMapActionType.ZoomToFeatures:
        case DnvMapActionType.AddFeatureLayers:
        case DnvMapActionType.RemoveFeatureLayers:
        case DnvMapActionType.DrawShapes: // vs AddedFeature ???
        case DnvMapActionType.IsDrawing:
        case DnvMapActionType.EditShapes:
        case DnvMapActionType.ToggleSelectShape:
        case DnvMapActionType.SaveNote:
        case DnvMapActionType.StartDraw:
        case DnvMapActionType.SelectFeature:
        case DnvMapActionType.ConcatAdditionalFeature:
        case DnvMapActionType.DeleteFeature:
        // AddedFeature // vs DrawShapes ???
        case DnvMapActionType.UpdateFeatureGeoCoord:
        case DnvMapActionType.UpdateShape:
        // UpdateShapesList
        case DnvMapActionType.UpdateConversionUnit:
        case DnvMapActionType.ToggleFeaturePanel:
        case DnvMapActionType.SetSelectedFeatureLayer:
        case DnvMapActionType.SetHighlightFeature:
        case DnvMapActionType.SetSurroundingFeatureList:
        case DnvMapActionType.SetSelectedSurroundingFeature:
        case DnvMapActionType.SetFeatureClickedType:
        case DnvMapActionType.ResetSelected:
        case DnvMapActionType.SetFeatureTable:
        case DnvMapActionType.MarkerClick:
        case DnvMapActionType.ToggleMarkerActive:
        case DnvMapActionType.ResetMarkersActive:
        case DnvMapActionType.ResizeMap:
        case DnvMapActionType.CheckMarkerPosition:
        case DnvMapActionType.SyncMarkersActive:
        // case DnvMapActionType.SelectBasemap:
        case DnvMapActionType.DrawUndo:
        case DnvMapActionType.DrawRedo:
        case DnvMapActionType.AddText:
        case DnvMapActionType.SetTextStyle:
        case DnvMapActionType.SetAllTextStyle:
        case DnvMapActionType.SetExtents:
        case DnvMapActionType.UpdateTextMarkup:
        case DnvMapActionType.UpdateFeatureStyle:
        case DnvMapActionType.UpdateStartDrawCircle:
        case DnvMapActionType.UpdateShapesCircle:
        case DnvMapActionType.SetFeatureLayersOpacity:
        case DnvMapActionType.UpdateShapeFeatureColor:
        case DnvMapActionType.UpdateShapeOutlineColour:
        case DnvMapActionType.UpdateShapeFeatureMarker:
        case DnvMapActionType.SetFillPattern:
        case DnvMapActionType.UpdateShapeFillPattern:
        case DnvMapActionType.UpdateMarkerShape:
        case DnvMapActionType.UpdateCircleRadius:
        case DnvMapActionType.ToggleEditShapes:
        case DnvMapActionType.SetLineStyle:
        case DnvMapActionType.UpdateFeatureLineStyle:
        case DnvMapActionType.UpdateFeatureRadius:
        case DnvMapActionType.BringToFront:
        case DnvMapActionType.BringToBack:
        // case DnvMapActionType.TogglePropertyInfo:
        case DnvMapActionType.SetArcGisToken:
        case DnvMapActionType.SetFilterAOIMode:
        case DnvMapActionType.SetFilterAOIShape:
        case DnvMapActionType.ShowingAOIShape:
        case DnvMapActionType.ToggleHideTilePane:
        case DnvMapActionType.SetHistoricalAerials:
        case DnvMapActionType.SetHistoricalAerialsOpacity:
        case DnvMapActionType.AddHistoricalAerials:
        case DnvMapActionType.EngPolygonEdit:
        case DnvMapActionType.SetMeasureShapeId:
        case DnvMapActionType.SetLineWeight:
        case DnvMapActionType.SetMarkerSize:
        case DnvMapActionType.UpdateMarkerSize:
        case DnvMapActionType.UpdateLineWeight:
        case DnvMapActionType.GetEditFeaturesGeometry:
        case DnvMapActionType.SetEditFeaturesGeometry:
        case DnvMapActionType.SetEditFeaturesPermission:
        case DnvMapActionType.SetEditFeatures:
        case DnvMapActionType.RevertFeatureGeometry:
        case DnvMapActionType.DeleteAllArrows:


        case DnvLayerActionType.LoadLayers:
        case DnvLayerActionType.LoadLayersSuccess:
        case DnvLayerActionType.ToggleShowFilterPanel:
        case DnvLayerActionType.UpdateLayerFilter:
        case DnvLayerActionType.QueryLayerFeatureCount:
        case DnvLayerActionType.UpdateLayerFeatureCount:
        // case DnvLayerActionType.SetActiveLayer:
        // case DnvLayerActionType.SetAvailableLayer:
        case DnvLayerActionType.SetLayers:
        case DnvLayerActionType.FeatureClicked:
        case DnvLayerActionType.HideFilterPanel:
        case DnvLayerActionType.CheckInspection:
        case DnvLayerActionType.GetInspection:
        case DnvLayerActionType.GetInspAttachment:
        case DnvLayerActionType.SetInspFields:
        case DnvLayerActionType.GetDrawingInfo:
        case DnvLayerActionType.SetDrawingInfo:
        case DnvLayerActionType.GetLayersWithIcons:
        case DnvLayerActionType.InspectionClicked:
        case DnvLayerActionType.ToggleExpand:
        case DnvLayerActionType.SetLayerFeatureVisible:
        case DnvLayerActionType.SetLayerInfo:
        case DnvLayerActionType.ClearLayerFilters:
        case DnvLayerActionType.SetLayersOpacity:


        case MapViewActionType.MapViewLoad:
        case MapViewActionType.MapViewLoadSharedId:
        case MapViewActionType.MapViewSet:
        case MapViewActionType.MapViewSetLoadedId:
        case MapViewActionType.MapViewSetSharedView:
        case MapViewActionType.MapViewSave:
        case MapViewActionType.MapViewSaveAs:
        case MapViewActionType.MapViewRenameTo:
        case MapViewActionType.MapViewDelete:
        case MapViewActionType.MapViewClose:
        case MapViewActionType.MapViewCheckIsDirty:
        case MapViewActionType.MapViewMarkDirty:
        case MapViewActionType.MapViewLoadDefault:
        case MapViewActionType.MapViewSetDefault:
        case MapViewActionType.MapViewSaveDefault:
        case MapViewActionType.MapViewDeleteDefault:
        case MapViewActionType.MapViewClearUrlSharedId:
        case MapViewActionType.MapViewSetUrlProvider:
        case MapViewActionType.MapViewSetStoreWrapper:


        case legendActionType.ToggleLegends:
        case legendActionType.SetActiveLegendLayers:
        case legendActionType.SetLegendLayersCache:
        case legendActionType.LoadLegendLayers:
        case legendActionType.LoadLegendLayersSuccess:
        case legendActionType.LoadLegendLayersError:
        case legendActionType.SetMetaData:
        case legendActionType.ToggleMetaDataPanel:


        case dnvSearchActionType.SET_SEARCH_ITEMS_URL:
        case dnvSearchActionType.SET_CURRENT_SEARCH_URL:
        case dnvSearchActionType.SET_SEARCH_ITEMS:
        case dnvSearchActionType.SET_ICONS_URL:
        case dnvSearchActionType.SET_ICONS:
        case dnvSearchActionType.LOAD_ASSET_ID:
        case dnvSearchActionType.LOAD_ASSET_ID_SUCCESS:
        case dnvSearchActionType.SET_SELECTED_ITEM:

        case PropertiesActionType.SetPropertyInfo:
        case PropertiesActionType.GetPropertyInfo:
        case PropertiesActionType.GetPropertyByXY:
        case PropertiesActionType.GetPropertyByAddress:
        case PropertiesActionType.GetPropertyByFolio:
        case PropertiesActionType.GetPropertyByPID:
        case PropertiesActionType.SetSelectedProperty:
        case PropertiesActionType.GetAirSpaceDbLink:
        case PropertiesActionType.SetPropertiesDbLinks:
        // case PropertiesActionType.GetEnerGovInfo:
        case PropertiesActionType.SetEnerGovInfo:


        // Also not tracking internal NgRx actions:
        case '@ngrx/effects/init':
        case '@ngrx/router-store/cancel':
        case '@ngrx/router-store/navigated':
        case '@ngrx/router-store/navigation':
        case '@ngrx/router-store/request':
        case '@ngrx/store/init':

          // Not tracking the above actions
          break;

        default:
          console.log('trackAction', action);
          const record = buildTrackingObject(state, action);
          console.log('record', record);
          httpClient.post<any>(trackingUrl, record, httpOptions)
            .pipe(
              retry(3),
              catchError((error: any) => of({}))
            )
            .subscribe();
          break;
      }

      return reducer(state, action);
    };
  };
}

function buildTrackingObject(state: State, action: Action): Tracking {
  const record: Tracking = {
    application: '',
    action: action.type,
    target: '',
    details: '',
    stacktrace: ''
  };

  switch (action.type) {
    // case ApplicationActionType.Error:
    case DnvLayerActionType.SetActiveLayer:
      record.target = 'DnvLayerComponent';
      record.details = (action as SetActiveLayer).payload?.name;
      break;

    case DnvLayerActionType.SetAvailableLayer:
      record.target = 'DnvLayerComponent';
      record.details = (action as SetAvailableLayer).payload?.name;
      break;

    case ApplicationActionType.GetMailMergeList:
      record.target = 'GetMailMergeList';
      record.details = ''; // JSON.stringify((action as GetMailMergeList).payload); // TODO check details max length 64 validation error
      break;

    default:
      console.warn('Default tracker ', action.type);
      break;
  }

  return record;
}
